import { useState, useEffect, useRef, useCallback } from 'react';
import {
  deleteComment,
  updateStatusComment,
  changeHideCommentStatus,
  getUnifiedComments,
  commentReplyById,
} from '../api/comment';
import { getPostById } from '../api/post';
import { useSocial } from '../context/socialProvider';
import UnifiedCommentDto from '../types/comment.types';
import { CommentStatus } from '../types/status.types';

interface UseCommentsOptions {
  postID?: string;
  autoMod?: boolean;
  adComments?: boolean;
}

const useComments = (options: UseCommentsOptions = {}) => {
  const { postID, autoMod, adComments } = options;

  const [initialComments, setInitialComments] = useState<UnifiedCommentDto[]>(
    []
  );
  const [comments, setComments] = useState<UnifiedCommentDto[]>([]);
  const [totalComments, setTotalComments] = useState(0);
  const [selectedComment, setSelectedComment] = useState<number | null>(null);
  const [commentsPerPage, setCommentsPerPage] = useState(13);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [statusFilter, setStatusFilter] = useState<string | number>('all');
  const [hideReplies, setHideReplies] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const { currentSocial } = useSocial();

  const [searchText, setSearchText] = useState('');
  const [removal, setRemoval] = useState(false);

  const loadComments = useCallback(async () => {
    setIsLoading(true);

    const requestSocial = currentSocial;

    try {
      const status =
        hideReplies && statusFilter === 'all'
          ? 3
          : statusFilter !== 'all'
            ? Number(statusFilter)
            : undefined;

      const response = await getUnifiedComments(
        Number(currentSocial?.id),
        postID,
        status as 0 | 1 | 2 | 3,
        currentPage,
        commentsPerPage,
        sortOrder === 'newest' ? 'DESC' : 'ASC',
        undefined,
        searchText,
        removal,
        true,
        autoMod,
        adComments
      );

      console.log('useComments', response);

      if (currentSocial?.id === requestSocial?.id) {
        setInitialComments(response.result);
        setComments(response.result);
        setTotalComments(response.total);
        const newTotalPages = Math.max(
          1,
          Math.ceil(response.total / commentsPerPage)
        );
        setTotalPages(newTotalPages);
      } else {
        console.log('Ignoring comments - platform switched during loading');
      }
    } catch (error) {
      console.error('Failed to load comments:', error);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentSocial?.id,
    postID,
    currentPage,
    commentsPerPage,
    sortOrder,
    statusFilter,
    hideReplies,
    removal,
    searchText,
    autoMod,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [currentSocial]);

  const updateCommentHiddenStatus = async () => {
    if (selectedComment !== null && comments[selectedComment]) {
      const currentComment = comments[selectedComment];
      const currentHiddenStatus = currentComment.hidden;

      // Optimistic update
      const updatedComments = comments.map((c: UnifiedCommentDto) =>
        c.platformCommentId === currentComment.platformCommentId
          ? { ...c, hidden: !currentHiddenStatus }
          : c
      );
      setComments(updatedComments);

      try {
        const result = await changeHideCommentStatus(
          currentComment.platformCommentId.toString(),
          !currentHiddenStatus,
          Number(currentSocial?.id)
        );

        if (!result.success) {
          setComments(comments);
          if (result.error) {
            console.error(result.error);
          }
        }
      } catch (error) {
        console.error('Failed to update comment hidden status:', error);
        setComments(comments);
      }
    }
  };

  const removeComment = async () => {
    try {
      setIsDeleting(true);

      if (typeof selectedComment === 'number' && selectedComment >= 0) {
        const commentToDelete = comments[selectedComment];
        await deleteComment(
          commentToDelete.platformCommentId,
          Number(currentSocial?.id)
        );

        if (selectedComment > -1) {
          const newComments = [...comments];
          newComments.splice(selectedComment, 1);
          setComments(newComments);

          if (newComments.length > 0) {
            if (selectedComment >= newComments.length) {
              setSelectedComment(newComments.length - 1);
            } else {
              setSelectedComment(selectedComment);
            }
          } else {
            setSelectedComment(null);
          }
        }
      }
    } catch (error: any) {
      console.error(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const replyToComment = async (platformCommentId: string, message: string) => {
    try {
      if (typeof selectedComment === 'number' && selectedComment >= 0) {
        await commentReplyById(
          platformCommentId,
          message,
          Number(currentSocial?.id)
        );
      }
    } catch (error) {
      console.error('Failed to reply to comment:', error);
    }
  };

  useEffect(() => {
    if (currentSocial?.id) {
      loadComments();
    }
  }, [
    currentSocial,
    currentPage,
    statusFilter,
    sortOrder,
    hideReplies,
    postID,
    searchText,
    removal,
    autoMod,
    loadComments,
  ]);

  useEffect(() => {
    const updateStatus = async () => {
      if (selectedComment === null) return;
      if (
        selectedComment !== null &&
        comments[selectedComment] &&
        comments[selectedComment].status === 0
      ) {
        try {
          const result = await updateStatusComment(
            comments[selectedComment].platformCommentId,
            CommentStatus.SEEN,
            Number(currentSocial?.id)
          );

          setComments((prevComments) =>
            prevComments.map((comment) =>
              comment.platformCommentId === result.platformCommentId
                ? { ...comment, status: result.status }
                : comment
            )
          );
        } catch (error) {
          console.error('Failed to update status:', error);
        }
      }
    };

    updateStatus();
  }, [selectedComment, comments, currentSocial?.id]);

  return {
    comments,
    initialComments,
    totalComments,
    selectedComment,
    commentsPerPage,
    currentPage,
    totalPages,
    sortOrder,
    statusFilter,
    hideReplies,
    isLoading,
    isDeleting,
    searchText,
    removal,
    setComments,
    setSelectedComment,
    setCommentsPerPage,
    setCurrentPage,
    setTotalPages,
    setSortOrder,
    setStatusFilter,
    setHideReplies,
    setSearchText,
    setRemoval,
    loadComments,
    removeComment,
    updateCommentHiddenStatus,
    replyToComment,
  };
};

export default useComments;
